import { useEffect, useRef, useState } from 'react'
import { peekPreloaded } from '@/lib/preloaded'

// GET a JSON endpoint. If the server preloaded this exact URL the first
// render already has the data (matching the server-rendered markup) and the
// initial fetch is skipped; any later URL change fetches as normal.
export function useJson<T>(url: string | null, failureMessage: string) {
  const [data, setData] = useState<T | null>(() => (url ? peekPreloaded<T>(url) : null))
  const [error, setError] = useState<string | null>(null)
  const preloadedUrl = useRef(data !== null ? url : null)

  useEffect(() => {
    if (!url) return
    if (preloadedUrl.current === url) return
    preloadedUrl.current = null

    let cancelled = false
    setData(null)
    setError(null)

    fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`)
        return response.json() as Promise<T>
      })
      .then((json) => {
        if (!cancelled) setData(json)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : failureMessage)
      })

    return () => {
      cancelled = true
    }
  }, [url, failureMessage])

  return { data, error }
}
